/* eslint-disable max-lines */
import React, {useCallback, useContext, useEffect, useState} from "react";
import {DEV, RECORDER_AUDIO_MIME_TYPES, RECORDER_VIDEO_MIME_TYPES, WEBSOCKET_URL} from "../settings";
import {bindEventListener, getUserMedia, releaseUserMediaStream} from "../utils";
import createVad from "voice-activity-detection";
import {MediaRecorder} from "extendable-media-recorder";
import createFileUploadWS, {FileUpload, FileUploadData} from "../libs/fileuploadWS";
import {LanguageCode, Challenge, ChallengeInputMode} from "../schema";

type VAD = ReturnType<typeof createVad>;

const MediaRecorderContext = React.createContext<{
  mediaType: "audio" | "video";
  hasPermission: undefined | boolean;
  recorderReady: boolean;
  isRecording: boolean;
  resultState: string;
  getResult: () => FileUploadData | undefined;
  mediaStream?: MediaStream;
  isUserSpeaking: boolean;
  voiceActivityValue: number | undefined;
  lang?: LanguageCode;
  accountId?: string;
  isShared?: boolean;

  toggleRecorder: (state: boolean) => void;
  record: (recognitionEngine: string, recognitionPipeline?: string) => void;
  stopRecord: () => boolean;
}>(null!);

type props = React.PropsWithChildren<{
  challenge: Challenge;
  isShared: boolean;
  mediaType: "audio" | "video";
  noTrackFocus?: boolean;
}>;

type MediaRecorderEx = MediaRecorder & {
  uploadFile: FileUpload;
};

export default function MediaRecorderProvider({
  challenge,
  isShared,
  noTrackFocus,
  mediaType: initMediaType,
  children,
}: props) {
  const [_hasFocus, setHasFocus] = useState(() => document.visibilityState === "visible");
  const hasFocus = noTrackFocus ? _hasFocus : true;

  const [hasPermission, setPermissionState] = useState<boolean | undefined>(undefined);

  const [mediaType, setMediaType] = useState(initMediaType);
  const [mediaStream, setMediaStream] = useState<MediaStream>();
  const [recorder, setRecorder] = useState<MediaRecorderEx>();
  const hasRecorder = !!recorder;

  const [requireRecorder, toggleRecordRequired] = useState<boolean>(false);
  const [isRecording, setRecordState] = useState<boolean | undefined>(undefined);
  const [resultState, setResultState] = useState<string>("inactive");

  const [isUserSpeaking, setUserSpeaking] = useState<boolean>(false);
  const [voiceActivityValue, setVoiceActivityValue] = useState<number>();
  const [sampleRate, setSampleRate] = useState<number>();

  useEffect(() => {
    if (!mediaStream) return;

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    let voiceStopTimeout: NodeJS.Timeout;
    let voiceStartTimeout: NodeJS.Timeout;
    let isSpeechConfirmed = false;

    const onVoiceStart = () => {
      clearTimeout(voiceStopTimeout);
      if (!isSpeechConfirmed) {
        voiceStartTimeout = setTimeout(() => {
          setUserSpeaking(true);
          isSpeechConfirmed = true;
        }, 150);
      }
    };

    const onVoiceStop = () => {
      clearTimeout(voiceStartTimeout);
      voiceStopTimeout = setTimeout(() => {
        setUserSpeaking(false);
        isSpeechConfirmed = false;
      }, 300);
    };

    const vad: VAD = createVad(audioContext, mediaStream, {
      avgNoiseMultiplier: 1.5,
      onVoiceStart,
      onVoiceStop,
      onUpdate: (val) => {
        if (val > 0.9) onVoiceStart();
      },
    });

    return () => {
      vad.destroy();
      setVoiceActivityValue(undefined);
      clearTimeout(voiceStartTimeout);
      clearTimeout(voiceStopTimeout);
    };
  }, [mediaStream]);

  // do not record when window not active, prevent problem with phones audio
  useEffect(
    () =>
      bindEventListener(document, "visibilitychange", () => {
        if (isRecording) {
          return;
        }

        setHasFocus(document.visibilityState === "visible");
      }),
    [isRecording],
  );

  useEffect(() => {
    if (challenge.inputMode === ChallengeInputMode.TEXT) {
      setPermissionState(true);
      return;
    }

    if (!hasFocus || hasPermission === false || (hasPermission !== undefined && !requireRecorder)) return;

    let mounted = true;

    setPermissionState(undefined);
    getUserMedia(mediaType)
      .then(([stream, mediaType]) => {
        if (!mounted) return;
        setPermissionState(true);
        setMediaType(() => mediaType);
        setMediaStream(stream);
        setSampleRate(stream.getAudioTracks()[0].getSettings().sampleRate);
      })
      .catch(() => {
        if (!mounted) return;
        setPermissionState(false);
        setMediaStream(undefined);
      });

    return () => {
      mounted = false;
      setMediaStream(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaType, hasFocus, requireRecorder, challenge.inputMode]);

  useEffect(() => {
    if (!requireRecorder && mediaStream) {
      releaseUserMediaStream(mediaStream);
    }
  }, [mediaStream, requireRecorder]);

  // media stream effect
  useEffect(() => {
    if (!mediaStream) {
      setRecorder(undefined);
    }

    return () => {
      if (mediaStream) {
        releaseUserMediaStream(mediaStream);
      }
    };
  }, [mediaStream]);

  // media recorder effect
  useEffect(() => {
    if (!recorder) {
      setRecordState(false);
    }

    return () => {
      if (recorder) {
        destroyMediaRecorder(recorder);
      }
    };
  }, [recorder]);

  const setResultReady = useCallback(() => {
    setResultState("ready");
  }, []);

  useEffect(() => {
    if (!requireRecorder) {
      if (recorder) {
        destroyMediaRecorder(recorder);
      }
      return;
    }
    if (!mediaStream || hasRecorder) {
      return;
    }

    const mediaRecorder = createMediaRecorder(mediaStream, getMimeType(mediaType));

    setRecorder(mediaRecorder);
    setRecordState(false);
  }, [recorder, mediaStream, hasRecorder, mediaType, requireRecorder, challenge, isShared, setResultReady]);

  const record = useCallback(
    (recognitionEngine: string, pipeline?: string) => {
      if (!recorder) {
        throw new Error("NoRecorder");
      }

      if (recorder.state !== "recording") {
        try {
        recorder.uploadFile = createFileUploadWS(
          DEV ? WEBSOCKET_URL : `${window.location.origin.replace("https", "wss")}/ws`,
          challenge.languageCode,
          recognitionEngine,
          challenge.id,
          challenge.accessToken,
          setResultReady,
          getMimeType(mediaType).replace("/", "_"),
          pipeline,
          sampleRate
        );
        recorder.ondataavailable = (e) => {
          if (recorder.uploadFile && e.data && e.data.size > 0) {
            recorder.uploadFile.chunk(e.data);
          }
        };

        setRecordState(true);
        recorder?.start(200);
      } catch (error) {
        console.error("[MediaRecorder] Failed to start recording:", error);
        setRecordState(false);
        setResultState("inactive");
      }
     }
    },
    [recorder, challenge.languageCode, challenge.id, challenge.accessToken, mediaType, sampleRate, setResultReady],
  );

  const stopRecord = useCallback(() => {
    if (!recorder) {
      return false;
    }

    (async () => {
      try {
        await recorder.uploadFile.waitForConnection();

        if (recorder.uploadFile.data.isError) {
          console.error("[MediaRecorder] Connection error detected, stopping recording");
          recorder.stop();
          setRecordState(false);
          setResultState("inactive");
          setRecorder(undefined);
          return;
        }

        recorder.stop();

        await new Promise((resolve) => setTimeout(resolve, 175));

        await recorder.uploadFile.done();

        // eslint-disable-next-line quotes
        recorder.uploadFile.chunk(new Blob(['{"eof" : 1}']));

        await recorder.uploadFile.done();
      } catch (error) {
        console.error("[MediaRecorder] Error in stopRecord:", error);
        if (recorder.state !== "inactive") {
          recorder.stop();
        }
        setRecordState(false);
        setResultState("inactive");
        setRecorder(undefined);
      }
    })();

    setRecordState(false);
    setResultState("pending");
    return true;
  }, [recorder]);

  const getResult = useCallback(() => {
    if (!recorder) {
      return;
    }

    setResultState("inactive");
    const data = recorder?.uploadFile.data;
    setRecorder(undefined);
    return data;
  }, [recorder]);

  const toggleRecorder = useCallback((state: boolean) => {
    if (state) {
      toggleRecordRequired(true);
    } else {
      setRecorder(undefined);
      toggleRecordRequired(false);
    }
  }, []);

  return (
    <MediaRecorderContext.Provider
      value={{
        mediaType,
        hasPermission,
        isRecording: !!isRecording,
        resultState: resultState,
        getResult,
        mediaStream,
        isUserSpeaking,
        voiceActivityValue,

        recorderReady: !!hasPermission && hasRecorder && !!mediaStream,
        toggleRecorder,
        record,
        stopRecord,
      }}
    >
      {children}
    </MediaRecorderContext.Provider>
  );
}

export function useMediaRecorder() {
  return useContext(MediaRecorderContext);
}

function getMimeType(mediaType: "audio" | "video") {
  let mimeTypes;

  switch (mediaType) {
    case "audio":
      mimeTypes = RECORDER_AUDIO_MIME_TYPES;
      break;

    case "video":
      mimeTypes = RECORDER_VIDEO_MIME_TYPES;
      break;

    default:
      throw new Error(`NotImplemented: media type "${mediaType}" not supported`);
  }

  const mimeType = mimeTypes.find((mime) => MediaRecorder.isTypeSupported(mime));

  if (!mimeType) {
    throw new Error(`Cannot start ${mediaType} record: no mime type supported by browser`);
  }

  return mimeType;
}

const createMediaRecorder = (mediaStream: MediaStream, mimeType: string) => {
  const mediaRecorder = new MediaRecorder(mediaStream, {
    mimeType: mimeType,
  }) as MediaRecorderEx;

  return mediaRecorder;
};

const destroyMediaRecorder = (mediaRecorder: MediaRecorderEx) => {
  if (mediaRecorder.state !== "inactive") {
    mediaRecorder.stop();
  }

  if (mediaRecorder.uploadFile) {
    mediaRecorder.uploadFile.destroy();
    mediaRecorder.uploadFile
      .done()
      .then(() => {
        mediaRecorder.uploadFile.destroy();
      })
      .catch(() => {
        mediaRecorder.uploadFile.destroy();
      });
  }
};
